// Alta de la empresa a partir del cuestionario de onboarding
// (ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real).
//
// Antes la empresa se creaba en `/onboarding`, un form que pedía nombre, país, marca y
// rubro. Ese form ya no existe: el cuestionario nuevo NO pregunta nada de eso, así que
// los cuatro datos salen de otro lado:
//
//   nombre → del registro (metadata del signUp, o el perfil de Google)
//   marca  → fijo `"Mi marca"` (se cambia después desde la app)
//   país   → deducido: el backend lo saca de la IP del request; acá se manda la
//            heurística por timezone como respaldo (`paisDelBrowser`)
//   rubro  → el del paso 2, traducido de id a label
//
// Se dispara al terminar el **paso 3**, antes del paso de producto: el paso 4 necesita
// `id_company` y créditos para generar de verdad, y así quien abandona a mitad del
// cuestionario no deja una empresa vacía.

import { supabase } from './supabase';
import { createCompanyForUser } from './createCompany';
import { ensureDefaultBrand } from './ensureDefaultBrand';
import { cuestionarioCompletado } from './cuestionarioObligatorio';
import { paisDelBrowser } from './paisDelBrowser';
import { nombreDelUsuario } from './nombreUsuario';
import {
  SETUP_CATEGORIES_KEY,
  SETUP_CONTENT_KEY,
  SETUP_ROLE_KEY,
  rubroDesdeCategorias,
} from '../components/onboarding/accountSetup';

/** Nombre por defecto de la marca. El usuario lo cambia después, desde la app. */
export const MARCA_POR_DEFECTO = 'Mi marca';

export interface AltaResult {
  ok: boolean;
  companyId?: string | null;
  /** Mensaje listo para mostrar. Sólo viene cuando `ok` es false. */
  error?: string;
}

function leerArray(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(parsed) ? (parsed as string[]).filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Crea la empresa con lo que juntó el cuestionario y persiste las respuestas que
 * tienen columna propia (rol y tipos de contenido). Idempotente del lado del backend:
 * un doble submit no crea una segunda empresa.
 */
export async function crearEmpresaDesdeCuestionario(): Promise<AltaResult> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return { ok: false, error: 'Se cerró la sesión. Volvé a iniciar sesión.' };

  const categorias = leerArray(SETUP_CATEGORIES_KEY);
  const contenidos = leerArray(SETUP_CONTENT_KEY);
  const rol = (() => {
    try {
      return localStorage.getItem(SETUP_ROLE_KEY);
    } catch {
      return null;
    }
  })();

  const res = await createCompanyForUser({
    userId: user.id,
    name: nombreDelUsuario(user),
    // El backend pisa esto con el país deducido de la IP; esto es el respaldo para
    // cuando el servicio de geo no responde. `paisDelBrowser` devuelve null si no
    // puede afirmar nada, y ahí "Otro" es la respuesta honesta.
    pais: paisDelBrowser() ?? 'Otro',
    companyName: MARCA_POR_DEFECTO,
    industry: rubroDesdeCategorias(categorias),
  });
  if (!res.ok) return res;

  // El rubro, escrito de forma AUTORITATIVA.
  //
  // `createCompanyForUser` ya lo intenta en su paso 3, pero ahí es best-effort: vive en
  // un `try/catch` mudo y se saltea entero si no pudo resolver el `id_company`. Eso
  // alcanzaba cuando la empresa se creaba recién en este momento —el backend la inserta
  // CON el rubro, así que el update del front era un refuerzo—, pero dejó de alcanzar
  // con el alta por link invertida: ahí la empresa ya existe (se creó al registrarse,
  // con el centinela, para poder cobrar en un paso), el backend es idempotente y NO la
  // pisa, y este update es lo ÚNICO que saca el centinela.
  //
  // Y si el centinela no se va, la app lo devuelve al cuestionario una y otra vez. Por
  // eso acá el fallo se REPORTA en vez de tragarse: el usuario reintenta y sale.
  // Ver ADR-2026-09-22-el-alta-por-link-cobra-primero-y-pregunta-despues.
  if (res.companyId) {
    const rubroElegido = rubroDesdeCategorias(categorias);
    const { error: errorRubro } = await supabase
      .from('company_profiles')
      .update({ rubro: rubroElegido })
      .eq('id_company', res.companyId);
    if (errorRubro) {
      return { ok: false, error: 'No pudimos guardar tu rubro. Probá de nuevo.' };
    }
    // El rubro real ya está escrito: el guardia de navegación deja de mandar acá. Va
    // ACÁ y no al final porque lo que sigue (marca, tipos de contenido) puede pedir un
    // reintento, y ese reintento no tiene que ser rebotado al paso 1.
    cuestionarioCompletado();
    // Y el de la MARCA, que es una copia aparte: `ensureDefaultBrand` la crea a partir
    // del rubro de la EMPRESA, así que en el alta por link nació con el centinela. Ese
    // helper ahora la repara si le pasás el rubro real, y se lo llama de nuevo acá —y
    // no sólo desde `createCompanyForUser`— porque allá es best-effort y se saltea
    // entero si no pudo resolver el id. Los modos se gatean por `brand_profiles.rubro`
    // sin caer al de la empresa: sin esto, el que pagó no ve los de su rubro.
    // Si la reparación de la marca falla, el rubro de la EMPRESA ya quedó escrito, así
    // que el rebote de `App` no vuelve a traer a esta persona por acá: éste es el único
    // momento en que se puede pedir el reintento. Sin eso, la marca se queda en blanco y
    // los gates de modos —que leen `brand_profiles.rubro` con igualdad exacta, sin caer
    // al de la empresa— le niegan Plantillas, Diseño, Moodboards, Catálogo de muebles e
    // Infografías a alguien que acaba de pagar.
    if (!(await ensureDefaultBrand(res.companyId, rubroElegido))) {
      return {
        ok: false,
        error: 'No pudimos terminar de configurar tu marca. Probá de nuevo.',
      };
    }
  } else if (res.ok) {
    // El alta dio success pero no sabemos el `id_company`: las dos lecturas de
    // `idCompanyDe` fallaron (`createCompanyForUser` ya lo reporta a Sentry). Sin ese
    // id no se escribió el rubro, ni la marca default, ni los tipos de contenido.
    //
    // Se REPORTA en vez de devolver success, que es lo que hacía antes: en el alta por
    // link la empresa ya existía con el centinela y el backend es idempotente, así que
    // este update es lo ÚNICO que lo saca — con un success acá el cuestionario terminaba
    // "bien", el centinela quedaba, y el rebote de `App` devolvía a la misma persona al
    // cuestionario en cada carga. Justo el loop que el comentario de arriba dice evitar.
    //
    // En el camino normal el rubro sí quedó bien (lo escribe el INSERT del backend), pero
    // igual falta la marca default: el reintento es idempotente y sale barato.
    return {
      ok: false,
      error: 'No pudimos terminar de configurar tu cuenta. Probá de nuevo.',
    };
  }

  // Respuestas del cuestionario que tienen dónde guardarse. Best-effort a propósito:
  // la empresa ya existe y el usuario tiene que poder seguir — un fallo acá se
  // traduce en un dato de segmentación faltante, no en un alta rota.
  try {
    if (rol) await supabase.from('profiles').update({ rol_en_empresa: rol }).eq('id', user.id);
    if (res.companyId && contenidos.length) {
      await supabase
        .from('company_profiles')
        .update({ tipos_contenido: contenidos })
        .eq('id_company', res.companyId);
    }
  } catch {
    /* best-effort */
  }

  return res;
}
